import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Users, CreditCard, DollarSign, MapPin, Download, Search, X, Mail, Sparkles } from "lucide-react";
import { StateMap } from "@/components/StateMap";
import { UserDrawer } from "@/components/UserDrawer";
import { TrendChart } from "@/components/TrendChart";
import { ProsManager } from "@/components/ProsManager";
import { PayoutsPanel } from "@/components/Payouts";
import { useTour } from "@/components/tour/TourProvider";
import { TourAnalytics } from "@/components/tour/TourAnalytics";
import { DisclosureRecords, LegalVersions } from "@/components/DisclosureRecords";
import { PartnerApplications } from "@/components/PartnerApplications";
import { StoriesAdmin } from "@/components/StoriesAdmin";
import { AcademyAdmin } from "@/components/AcademyAdmin";
import { CohortAdmin } from "@/components/CohortAdmin";
import { AssistantInsights } from "@/components/AssistantInsights";
import { CoverageMap, ReferralBoard } from "@/components/CoverageMap";
import { ShareStats, LeadsPanel, SiteShareLinks } from "@/components/GrowthPanels";
import { AdCheckup } from "@/components/AdCheckup";
import { W9Review } from "@/components/W9";
import { Report1099, SearchConsoleReport } from "@/components/AdminReports";
import { ContentGaps } from "@/components/ContentGaps";
import { InvestorPdfButton } from "@/components/InvestorPdfButton";
import { HandoutTracker } from "@/components/HandoutTracker";
import { OutreachEmails } from "@/components/OutreachEmails";
import { Leaderboard, FollowUpDialog } from "@/components/Leaderboard";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { money } from "@/pages/Awards";

const PKG = { proposal_writer: "Proposal", licensing_guide: "Licensing", suite: "Suite" };

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <Icon className="h-5 w-5 text-emerald-600" />
      <div className="font-display text-3xl font-extrabold text-slate-900 mt-4" data-testid={testid}>{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

function StateTable({ rows, max, active, onPick }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="admin-state-table">
      <div className="grid grid-cols-12 px-5 py-3 border-b border-slate-200 font-mono text-[10px] uppercase tracking-widest text-slate-400">
        <span className="col-span-5">State</span><span className="col-span-2 text-right">Users</span><span className="col-span-2 text-right">Paying</span><span className="col-span-3 text-right">Revenue</span>
      </div>
      <div className="max-h-[560px] overflow-y-auto divide-y divide-slate-100">
        {rows.map((s) => (
          <button key={s.state} onClick={() => onPick(active === s.state ? null : s.state)} data-testid={`admin-state-row-${s.state.replace(/ /g, "-").toLowerCase()}`}
            className={`w-full grid grid-cols-12 items-center px-5 py-3 text-sm text-left transition-colors ${active === s.state ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
            <span className="col-span-5">
              <span className={`font-medium ${s.state === "Not set" ? "text-slate-400 italic" : "text-slate-900"}`}>{s.state}</span>
              <span className="block h-1.5 mt-1.5 rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${Math.max(4, (s.users / max) * 100)}%` }} />
            </span>
            <span className="col-span-2 text-right font-semibold text-slate-900">{s.users}</span>
            <span className="col-span-2 text-right text-slate-600">{s.paying}</span>
            <span className="col-span-3 text-right text-slate-600">{money(s.revenue)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function exportCsv(users) {
  const head = ["Name", "Email", "Company", "State", "City", "Industry", "Packages", "Revenue", "Sign-in", "Joined"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [head, ...users.map((u) => [u.name, u.email, u.company, u.state, u.city, u.industry, u.packages.join(" "), u.revenue, u.auth_provider, u.created_at])];
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([lines.map((l) => l.map(esc).join(",")).join("\n")], { type: "text/csv" }));
  a.download = "grantverify_users.csv";
  a.click();
}

export default function MasterAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [state, setState] = useState(null);
  const [q, setQ] = useState("");
  const [openUser, setOpenUser] = useState(null);
  const { start: startTour } = useTour();
  const [params, setParams] = useSearchParams();
  const followup = params.get("followup");
  const closeFollowup = useCallback(() => setParams({}), [setParams]);
  const sendDigest = async () => { try { const { data } = await api.post("/admin/digest/send-now"); toast.success(`Digest sent to ${data.to}`); } catch { toast.error("Digest failed"); } };
  const loadOverview = () => api.get("/admin/overview").then((r) => setData(r.data));
  useEffect(() => { if (user?.role === "admin") loadOverview(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleTests = async () => { await api.put("/admin/prefs", { hide_test_accounts: !data.totals.hide_test_accounts }); loadOverview(); };
  const filtered = useMemo(() => {
    if (!data) return [];
    const s = q.toLowerCase();
    return data.users.filter((u) => (!state || u.state === state) && (!s || `${u.name} ${u.email} ${u.company}`.toLowerCase().includes(s)));
  }, [data, state, q]);
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />;
  const t = data.totals;
  const max = Math.max(1, ...data.by_state.map((s) => s.users));
  return (
    <div>
      <PageHeader eyebrow="Master admin" title="Users by state"
        action={<div className="flex flex-wrap gap-2"><Button variant="outline" onClick={() => startTour("admin")} data-testid="admin-tour-button"><Sparkles className="h-4 w-4 mr-2" />Admin tour</Button><Button variant="outline" onClick={sendDigest} data-testid="admin-send-digest-button"><Mail className="h-4 w-4 mr-2" />Send digest now</Button><InvestorPdfButton /><Button variant="outline" onClick={() => exportCsv(filtered)} data-testid="admin-export-csv-button"><Download className="h-4 w-4 mr-2" />Export CSV</Button></div>}>
        State comes from each user's business profile. Click a state on the map or table, or open any user. A digest is emailed every Monday.
      </PageHeader>
      <label className="mb-4 inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="admin-hide-tests-toggle">
        <input type="checkbox" checked={!!t.hide_test_accounts} onChange={toggleTests} className="accent-emerald-600" data-testid="admin-hide-tests-checkbox" />
        Hide test accounts (emails ending @example.com or @resend.dev, or names starting with "TEST")
        {t.hide_test_accounts && <span className="text-xs font-medium text-slate-600" data-testid="admin-hidden-count">· {t.hidden_test_accounts} hidden</span>}
      </label>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total users" value={t.users} testid="admin-total-users" />
        <Stat icon={CreditCard} label="Paying users" value={t.paying} testid="admin-paying-users" />
        <Stat icon={DollarSign} label="Revenue collected" value={money(t.revenue)} testid="admin-total-revenue" />
        <Stat icon={MapPin} label="States reached" value={t.states} testid="admin-states-count" />
      </div>
      <div className="mt-8"><StateMap rows={data.by_state} active={state} onPick={setState} /></div>
      <div className="mt-6"><TrendChart state={state} /></div>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5"><StateTable rows={data.by_state} max={max} active={state} onPick={setState} /></div>
        <div className="lg:col-span-7">
          <div className="flex gap-3 items-center mb-3">
            <div className="relative flex-1"><Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" /><Input className="pl-9" placeholder="Search name, email or company" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-user-search-input" /></div>
            {state && <button onClick={() => setState(null)} className="text-xs bg-emerald-100 text-emerald-800 rounded-full px-3 py-1.5 flex items-center gap-1" data-testid="admin-clear-state-filter">{state}<X className="h-3 w-3" /></button>}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[560px] overflow-y-auto" data-testid="admin-users-list">
            {filtered.length === 0 && <p className="p-6 text-sm text-slate-500">No users match.</p>}
            {filtered.map((u) => (
              <button key={u.user_id} onClick={() => setOpenUser(u.user_id)} className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors" data-testid={`admin-user-row-${u.user_id}`}>
                <div className="h-9 w-9 rounded-full bg-slate-100 text-slate-700 grid place-items-center font-semibold text-sm shrink-0">{(u.name || u.email)[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{u.name} {u.company && <span className="font-normal text-slate-400">· {u.company}</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email} · {u.city ? `${u.city}, ` : ""}{u.state}</div>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {u.packages.filter((p) => PKG[p]).map((p) => <span key={p} className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">{PKG[p]}</span>)}
                  {!u.packages.length && <span className="text-[10px] font-mono uppercase text-slate-400">Free</span>}
                </div>
                <span className="text-xs text-slate-500 w-16 text-right shrink-0">{money(u.revenue)}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2" data-testid="admin-users-count">{filtered.length} of {data.users.length} users</p>
        </div>
      </div>
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">W-9 tax forms</h2>
      <W9Review />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Year-end 1099 report</h2>
      <Report1099 />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Partner applications</h2>
      <PartnerApplications />
      <StoriesAdmin />
      <AcademyAdmin />
      <CohortAdmin />
      <AssistantInsights />
      <ReferralBoard />
      <h2 id="ad-checkup" className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Weekly ad check-up</h2>
      <AdCheckup />
      <HandoutTracker />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Library &amp; partner outreach</h2>
      <OutreachEmails />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Home page leads &amp; share tracking</h2>
      <SiteShareLinks />
      <ShareStats />
      <LeadsPanel />
      <SearchConsoleReport />
      <ContentGaps />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Pro coverage by state</h2>
      <CoverageMap />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Professional accounts</h2>
      <ProsManager />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Pro leaderboard</h2>
      <Leaderboard />
      {followup && <FollowUpDialog itemId={followup} onClose={closeFollowup} />}
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Pro payouts</h2>
      <PayoutsPanel />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Tour analytics</h2>
      <TourAnalytics />
      <h2 className="font-display text-2xl font-bold text-slate-900 mt-12 mb-4">Funding Disclosure agreements</h2>
      <LegalVersions />
      <DisclosureRecords />
      <UserDrawer userId={openUser} onClose={() => setOpenUser(null)}
        onSaved={(id, packages) => setData((d) => ({ ...d, users: d.users.map((u) => (u.user_id === id ? { ...u, packages } : u)) }))} />
    </div>
  );
}
